/**
 * `wayai bases import …` — bulk historical import into a base.
 *
 * Namespaced because WayAI already owns an import verb (outbound contacts).
 *
 * `import run` drives the three-call server protocol — `begin`, N × `chunk`,
 * `finalize` — over an NDJSON file. The interesting part is not the upload; it is
 * that this is the ONE write path where re-sending after an ambiguous outcome is
 * safe, and the command is built to exploit that rather than to avoid it:
 *
 *  - `begin` is idempotent by a client `request_key`, so a lost begin RESPONSE is
 *    simply re-sent and returns the same session instead of orphaning one.
 *  - each chunk carries a server-side receipt keyed by `(chunk_index, payload
 *    hash)`, so a re-sent chunk replays its recorded result and charges nothing.
 *
 * Everything below is a consequence of those two facts.
 */

import { createReadStream } from 'node:fs';
import { access, constants, stat } from 'node:fs/promises';
import { createInterface } from 'node:readline';
import { randomUUID } from 'node:crypto';
import { Command } from 'commander';
import { createDataClient, dataErrorCode, dataErrorDetails, type DataClient } from '../client.js';
import { printOutput } from '../output.js';
import { isInteractive, outputFormat, pathSegment, requireBase, withBaseOption } from '../helpers.js';
import { ApiError } from '../../lib/api-client.js';
import { isNetworkError } from '../../lib/network-error.js';
import { sanitizeTerminalText } from '../../lib/terminal-output.js';
import { confirm } from '../../lib/utils.js';
import { expected } from '../../lib/expected.js';

/** Server cap on rows per chunk. Chunking larger would 400 on every send. */
const MAX_CHUNK_ROWS = 1000;
/** Attempts per chunk before giving up, covering the retryable-outcome classes below. */
const MAX_ATTEMPTS = 5;
/**
 * Rollback sweeps one invocation will issue. High enough that no real undo
 * reaches it, low enough that a server never reporting `complete` stops — the
 * same bound, for the same reason, as `collectPages`'s `MAX_PAGES`.
 */
const MAX_ROLLBACK_SWEEPS = 1000;
const BASE_BACKOFF_MS = 500;

/** Transient failures the server names explicitly; all shed before doing work. */
const RETRYABLE_CODES = new Set(['BASE_OVERLOADED', 'SERVICE_UNAVAILABLE', 'RATE_LIMITED']);

interface Session {
  import_id: string;
  status: string;
  record_type: string;
  rows_written: number;
  rows_skipped: number;
  chunks_applied: number;
}

interface ChunkResult {
  rows_written?: number;
  rows_skipped?: number;
  replayed?: boolean;
}

/**
 * Set when a retry followed an OUTCOME-UNKNOWN failure. Those are safe to re-send
 * (every route here is server-idempotent) but they are not safe to COUNT: the
 * lost response's work is invisible, so any total accumulated across one is a
 * lower bound, not a measurement.
 *
 * Per-invocation rather than module-level: several invocations share one process
 * (tests, and any future long-lived host), and a leaked `true` would mark a clean
 * run's counts as unreliable.
 */
interface RetryState {
  sawAmbiguous: boolean;
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Retry transient transport and server failures — INCLUDING outcome-unknown ones
 * such as a DO timeout, where the request may still execute later. That is
 * normally forbidden, and it is safe here for one reason only: every route this
 * file calls is server-idempotent. `begin` dedupes on `request_key`, a chunk
 * replays its receipt, `finalize` is idempotent, and the rollback sweep resumes
 * because each page commits. Do NOT reuse this helper for a route without that
 * property — the safety lives in the endpoints, not in the retry.
 *
 * 501 is excluded because "not implemented" does not become implemented on the
 * next attempt.
 */
function isRetryable(err: unknown): boolean {
  if (isNetworkError(err)) return true;
  if (!(err instanceof ApiError)) return false;
  const code = dataErrorCode(err);
  if (code && RETRYABLE_CODES.has(code)) return true;
  return err.status >= 500 && err.status !== 501;
}

/**
 * An EXPIRED session — the UTC month boundary, and the only 409 this command may
 * transparently replace. The server distinguishes it from a deliberately-closed
 * one via `details.status` precisely so a client does not restart what a human
 * stopped: `finalize`, and a concurrent `import rollback` (which closes the
 * session before sweeping), both produce `closed`. Treating those as a boundary
 * would resurrect the rows an operator just aborted, under a second import_id
 * they were never told about.
 */
function isExpiredSession(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  if (dataErrorCode(err) !== 'IMPORT_SESSION_CLOSED') return false;
  return dataErrorDetails(err)?.status === 'expired';
}

async function withRetry<T>(state: RetryState, fn: () => Promise<T>, what: string): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (!isRetryable(err) || attempt === MAX_ATTEMPTS) throw err;
      // A shed named by an explicit code is outcome-KNOWN (it sheds
      // storage-free, before doing work); anything else 5xx — a DO timeout or a
      // reset — may have committed before its response was lost.
      const code = err instanceof ApiError ? dataErrorCode(err) : null;
      if (!code || !RETRYABLE_CODES.has(code)) state.sawAmbiguous = true;
      const delay = BASE_BACKOFF_MS * 2 ** (attempt - 1);
      console.error(
        `  ${what}: ${(err as Error).message} — retrying in ${delay}ms (${attempt}/${MAX_ATTEMPTS - 1})`,
      );
      await sleep(delay);
    }
  }
  throw lastErr;
}

/**
 * Read an NDJSON file into fixed-size chunks, streaming rather than slurping — a
 * historical backfill is routinely larger than memory, which is the whole reason
 * this command exists instead of `bases batch --operations @file`.
 */
export async function* readChunks(
  file: string,
  size: number,
  startChunk: number,
): AsyncGenerator<{ index: number; rows: unknown[] }> {
  const rl = createInterface({ input: createReadStream(file, 'utf8'), crlfDelay: Infinity });
  let rows: unknown[] = [];
  let index = 0;
  let lineNo = 0;
  let inChunk = 0;
  for await (const line of rl) {
    lineNo++;
    const trimmed = line.trim();
    if (!trimmed) continue;
    // Skip the resumed prefix by COUNTING lines, never by parsing and discarding
    // them: a resume at chunk 900 of a 1M-row file would otherwise parse 900,000
    // objects it will never send, and a malformed line anywhere in that prefix
    // would abort a resume that does not touch it.
    if (index < startChunk) {
      if (++inChunk === size) {
        inChunk = 0;
        index++;
      }
      continue;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      // Name the line, like the server names the row — a 400 on line 40,001 of a
      // backfill is unactionable without it. `expected`, because a malformed
      // input file is user input rather than a defect worth a Sentry event.
      throw expected(`${file}:${lineNo}: not valid JSON. Each line must be one JSON object.`);
    }
    rows.push(parsed);
    if (rows.length === size) {
      yield { index, rows };
      rows = [];
      index++;
    }
  }
  if (rows.length > 0) yield { index, rows };
}

/** Open (or re-open) a session for this logical run. Idempotent by `request_key`. */
async function beginSession(
  state: RetryState,
  client: DataClient,
  base: string,
  recordType: string,
  requestKey: string,
): Promise<Session> {
  const res = await withRetry(
    state,
    () =>
      client.request<{ session: Session }>('POST', `/v1/${base}/import/begin`, {
        request_key: requestKey,
        record_type: recordType,
      }),
    'begin',
  );
  return res.session;
}

export function buildBasesImportCommand(): Command {
  // Every verb here addresses one base, so `--base` sits on the group.
  const importCmd = withBaseOption(new Command('import')).description(
    'Bulk historical import: load an NDJSON backfill, list sessions, or undo one',
  );

  importCmd
    .command('run <record_type>')
    .description(
      'Load an NDJSON file into a record type (one JSON object per line, each {external_id, data})',
    )
    .requiredOption('--file <path>', 'NDJSON file to import')
    .option(
      '--chunk-size <n>',
      'Rows per request, max 1000. Must match the original run when resuming.',
      String(MAX_CHUNK_ROWS),
    )
    .option(
      '--request-key <key>',
      'Idempotency key for this logical run (default: generated). Re-use it to resume.',
    )
    .option(
      '--start-chunk <n>',
      'Skip chunks before this index — resume a run that stopped partway',
      '0',
    )
    .option('--no-finalize', 'Leave the session open (resume later with the same --request-key)')
    .action(async function (
      this: Command,
      recordType: string,
      opts: {
        file: string;
        chunkSize: string;
        requestKey?: string;
        startChunk: string;
        finalize: boolean;
      },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const state: RetryState = { sawAmbiguous: false };

      const chunkSize = Number(opts.chunkSize);
      if (!Number.isInteger(chunkSize) || chunkSize < 1 || chunkSize > MAX_CHUNK_ROWS) {
        throw expected(`--chunk-size must be an integer between 1 and ${MAX_CHUNK_ROWS}.`);
      }
      const startChunk = Number(opts.startChunk);
      if (!Number.isInteger(startChunk) || startChunk < 0) {
        throw expected('--start-chunk must be a non-negative integer.');
      }
      // Chunk indices are derived from --chunk-size, and the server's receipts
      // are keyed by index — so resuming under a DIFFERENT size silently re-maps
      // which rows each index covers. Falling back to the default is the
      // dangerous case, because it happens by omission: resume a --chunk-size 100
      // run without repeating the flag and index 10 means rows 10,000-10,999
      // instead of 1,000-1,099, skipping 9,000 rows with no hash mismatch to
      // catch it (those indices are never re-sent). Demand the size explicitly
      // rather than guess it.
      if (startChunk > 0 && !this.getOptionValueSource('chunkSize')?.startsWith('cli')) {
        throw expected(
          '--start-chunk requires --chunk-size to be given explicitly, and it must match the original run.',
        );
      }

      // Fail before `begin`. A session is a scarce, 24h-lived resource (a bounded
      // number of open sessions per base, reclaimed only lazily on inactivity),
      // so a typo'd path must not cost one: a handful would wedge every import on
      // the base for a day.
      // BOTH checks, because each catches a case the other misses and both end
      // the same way — a session minted, then a throw from inside the generator,
      // since `createReadStream` does not run until the first `for await` pull.
      // `stat` rejects a DIRECTORY, which is readable, so `access(R_OK)` alone
      // lets `--file ./data/` (a tab-completed path missing its filename) through
      // to EISDIR. `access(R_OK)` rejects an UNREADABLE regular file, which
      // `isFile()` alone lets through to EACCES — a root-owned dump, a 600-mode
      // export, a container running as non-root. Rejecting only directories
      // rather than requiring `isFile()` keeps FIFOs working, so
      // `--file <(zcat dump.gz)` and `--file /dev/stdin` still stream.
      try {
        if ((await stat(opts.file)).isDirectory()) throw new Error('is a directory');
        await access(opts.file, constants.R_OK);
      } catch {
        throw expected(`--file ${opts.file}: not a readable file.`);
      }

      const client = await createDataClient();

      // One key per logical run. Generated keys are printed so an interrupted run
      // is resumable — without the key a resume would mint a NEW session and
      // re-send every chunk into it, which is correct but pays a full re-scan.
      const requestKey = opts.requestKey ?? `cli-${randomUUID()}`;
      if (!opts.requestKey) {
        console.error(`request_key: ${requestKey}  (pass --request-key to resume this run)`);
      }

      let session = await beginSession(state, client, base, recordType, requestKey);
      console.error(`import_id: ${session.import_id}`);

      const importIds = [session.import_id];
      let written = 0;
      let skipped = 0;
      let sent = 0;
      // The resume point, tracked client-side because nothing server-side can
      // supply it: the session's `chunks_applied` counts only ITS OWN chunks, so
      // after a month boundary (two sessions) or an expiry (a fresh session at
      // zero) it does not correspond to a file-global chunk index at all. The
      // only component that knows which index is next is the one reading the file.
      let nextIndex = startChunk;

      try {
        for await (const { index, rows } of readChunks(opts.file, chunkSize, startChunk)) {
          nextIndex = index;
          const body = { import_id: session.import_id, chunk_index: index, rows };

          let result: ChunkResult;
          try {
            result = await withRetry(
              state,
              () => client.request<ChunkResult>('POST', `/v1/${base}/import/chunk`, body),
              `chunk ${index}`,
            );
          } catch (err) {
            if (!isExpiredSession(err)) throw err;
            // The month boundary. A session may not outlive the UTC month it was
            // granted in, because its allowance snapshot IS that month's grant.
            // The server expires it and returns this 409; the recovery is to
            // begin a FRESH session and re-send the in-flight chunk. That is
            // nearly free: the price is rows WRITTEN, and every row already
            // written hits the unchanged-row skip, so the boundary costs one
            // begin round-trip rather than a re-bill. Note the consequence for
            // undo — the logical run now spans TWO import_ids, so a rollback must
            // be run against each.
            console.error(
              `  chunk ${index}: session expired (month boundary) — starting a fresh session and re-sending.`,
            );
            console.error(
              `  NOTE: this run now spans two import_ids; undo needs both. Previous: ${session.import_id}`,
            );
            session = await beginSession(state, client, base, recordType, requestKey);
            importIds.push(session.import_id);
            console.error(`  import_id: ${session.import_id}`);
            result = await withRetry(
              state,
              () =>
                client.request<ChunkResult>('POST', `/v1/${base}/import/chunk`, {
                  ...body,
                  import_id: session.import_id,
                }),
              `chunk ${index}`,
            );
          }

          written += result.rows_written ?? 0;
          skipped += result.rows_skipped ?? 0;
          sent++;
          nextIndex = index + 1;
          if (sent % 10 === 0) {
            console.error(`  ${sent} chunks — ${written} written, ${skipped} unchanged`);
          }
        }
        // Inside the try on purpose: a failed finalize is also a resume
        // situation, and by then `nextIndex` is past the last chunk, so replaying
        // the printed command sends no rows and simply finalizes. Leaving it
        // outside would print no hint for the one failure that needs the least.
        if (opts.finalize) {
          await withRetry(
            state,
            () =>
              client.request(
                'POST',
                `/v1/${base}/import/${pathSegment(session.import_id, 'import id')}/finalize`,
              ),
            'finalize',
          );
        }
      } catch (err) {
        // Tell the operator what to ADD to the command they ran, rather than
        // reprinting the command. Reconstructing it means enumerating every
        // argument that decides where rows land — `--base` (which also comes from
        // WAYAI_BASE) and `--org` (which selects a different base for the same id,
        // since base identity is scoped per-org) — each silently resuming a
        // production backfill somewhere else under a fresh import_id. Naming the
        // delta cannot omit an ambient argument, because it never claims to
        // reproduce one. What the operator must not get wrong is the index and
        // the chunk size, and those are exactly what this states.
        console.error(
          `\nStopped at chunk ${nextIndex}. To resume, re-run the SAME command with:\n` +
            `  --start-chunk ${nextIndex} --chunk-size ${chunkSize} --request-key ${requestKey}\n` +
            `(keep every other flag you passed — the base and org decide where the rows land, and the ` +
            `chunk size decides which rows each index covers.)`,
        );
        throw err;
      }

      // Report the RUN's own totals, not the finalize response. `finalize`
      // returns one SESSION's row counts, and a run that crossed a month boundary
      // spans two sessions — printing the last one would under-report a 300-row
      // file as 200 and hand a verifying script a shortfall that never happened.
      // Same keys either way, so `--no-finalize` does not change the shape.
      printOutput(
        {
          import_ids: importIds,
          rows_written: written,
          rows_skipped: skipped,
          chunks_sent: sent,
          finalized: opts.finalize,
        },
        outputFormat(this),
      );
    });

  importCmd
    .command('list')
    .description('List import sessions, newest first — find a stuck run or the id to undo')
    .option('--limit <n>', 'Maximum sessions to return')
    .option('--offset <n>', 'Sessions to skip')
    .action(async function (this: Command, opts: { limit?: string; offset?: string }) {
      const base = pathSegment(requireBase(this), '--base');
      const qs = new URLSearchParams();
      if (opts.limit) qs.set('limit', opts.limit);
      if (opts.offset) qs.set('offset', opts.offset);
      const suffix = qs.toString() ? `?${qs}` : '';
      const client = await createDataClient();
      printOutput(
        await client.request('GET', `/v1/${base}/import/sessions${suffix}`),
        outputFormat(this),
      );
    });

  importCmd
    .command('rollback <import_id>')
    .description(
      'Undo an import — deletes only the rows it CREATED. Re-importing corrected data is usually the better fix.',
    )
    .option('--yes', 'Skip the confirmation prompt')
    .action(async function (this: Command, importId: string, opts: { yes?: boolean }) {
      const base = pathSegment(requireBase(this), '--base');
      const format = outputFormat(this);
      const state: RetryState = { sawAmbiguous: false };
      const client = await createDataClient();

      // Off a TTY the prompt has no one to answer it, so a missing `--yes` is
      // REFUSED — never assumed. Gating the block on `isInteractive()` alone
      // skipped the confirmation and fell straight through to the sweep, so a CI
      // job or an agent (both always non-interactive) deleted every record the
      // import created, plus every relationship pointing at them, with no prompt
      // and no error. `secrets delete` and `tokens prune` refuse the same way.
      if (!opts.yes && !isInteractive()) {
        throw expected(
          'Refusing to roll back an import without confirmation — pass --yes to run non-interactively.',
        );
      }
      // Only read the plan when a prompt will actually be shown. With `--yes`
      // the read would fund nothing — a wasted round trip plus a count over the
      // marker on the base's write-serializing thread.
      if (!opts.yes) {
        // The live matched count, from the plan's opt-in `?counts=true`. A
        // confirmation that cannot say how much it will delete is not a
        // confirmation.
        const plan = await client.request<{
          record_type: string | null;
          matched_records?: number;
        }>('GET', `/v1/${base}/import/${pathSegment(importId, 'import id')}/rollback-plan?counts=true`);
        const n = plan.matched_records ?? 0;
        if (n === 0) {
          // Deliberately NOT an early return. The sweep is also what CLOSES an
          // open session, and the case that produces zero marked rows is
          // precisely a run whose first chunk was rejected — leaving a session
          // open that nothing else reclaims for 24h. Returning here would make
          // the confirming path diverge from `--yes`, which does close it, and a
          // handful of aborted starts then wedge every import on the base. Skip
          // the prompt (there is nothing destructive to confirm) and let the
          // idempotent POST run.
          console.error('No rows left to undo — closing the import session.');
        } else {
          // The record type name is tenant-authored and this prompt is the last
          // thing the operator reads before a destructive confirmation, so a
          // control sequence in it must not be able to rewrite the question.
          const ok = await confirm(
            `Delete ${n} record${n === 1 ? '' : 's'} created by this import into "${sanitizeTerminalText(String(plan.record_type))}", plus every relationship pointing at them?`,
          );
          if (!ok) {
            console.error('Aborted.');
            return;
          }
        }
      }

      // The sweep is paged and resumable by design: each call reports what it
      // ACTUALLY swept and whether anything remains, and a retryable shed keeps
      // whatever it already swept. So loop until the server says complete rather
      // than assuming one call suffices.
      let records = 0;
      let relationships = 0;
      let calls = 0;
      let incomplete = false;
      for (let sweep = 0; ; sweep++) {
        const res = await withRetry(
          state,
          () =>
            client.request<{
              swept: { records: number; relationships: number };
              complete: boolean;
            }>('POST', `/v1/${base}/import/${pathSegment(importId, 'import id')}/rollback`),
          'rollback',
        );
        records += res.swept.records;
        relationships += res.swept.relationships;
        calls++;
        if (res.complete) break;

        // Two independent stops, for the same reason `collectPages` carries
        // them: `complete` is the server's claim, and this loop issues a
        // DESTRUCTIVE POST at the base's write-serializing thread on every turn.
        // A page that sweeps nothing while still reporting `complete: false` is
        // not making progress, and one that always reports it would spin
        // forever — under `--json` with the progress line suppressed, a CI job
        // hangs with no output at all until its own timeout kills it.
        const sweptNothing = res.swept.records === 0 && res.swept.relationships === 0;
        if (sweptNothing || sweep + 1 >= MAX_ROLLBACK_SWEEPS) {
          incomplete = true;
          console.error(
            sweptNothing
              ? '  the sweep stopped making progress before the server reported it complete — re-run to continue.'
              : `  stopped after ${MAX_ROLLBACK_SWEEPS} sweeps without completing — re-run to continue.`,
          );
          break;
        }
        if (format !== 'json') {
          console.error(`  swept ${records} records, ${relationships} relationships so far…`);
        }
      }
      // Report the totals honestly. A page can COMMIT and still lose its response
      // (a DO timeout, a reset); the resumed sweep then skips those already-
      // deleted rows, so they are never counted anywhere and the sum is short by
      // an amount nothing client-side can recover. Accumulating per attempt does
      // not help — the lost attempt returns nothing to accumulate. So say it is a
      // floor rather than print a confident number that under-reports a
      // destructive operation.
      if (state.sawAmbiguous && format !== 'json') {
        console.error(
          'Note: a request was re-sent after its outcome was unknown, so the counts below are a ' +
            'lower bound — a page that committed before its response was lost is not included.',
        );
      }
      printOutput(
        {
          import_id: importId,
          swept: { records, relationships },
          requests: calls,
          // The server's own completion claim, carried so a script can tell an
          // undo that FINISHED from one that stopped early and needs re-running.
          complete: !incomplete,
          ...(state.sawAmbiguous ? { counts_are_lower_bound: true } : {}),
        },
        format,
      );
      // A partial undo is a FAILURE, and it has to be one the shell can see.
      // The document above says `complete: false`, but only a `--json` caller
      // parses it; the realistic consumer of a destructive resumable command is
      // `wayai bases import rollback "$id" --yes || handle`, or `set -e`, and
      // both read the exit code alone. Returning 0 here would report a finished
      // undo over records still live in the base — and this is the branch the
      // loop bound newly made reachable, since the same server response used to
      // hang the process, which at least turned the job red. `tokens prune`
      // ends its own partial-success path the same way.
      if (incomplete) return process.exit(1);
    });

  return importCmd;
}
